/**
 * CDP SDK News Plugin - Article Detector
 * Detects article pages and extracts metadata
 */

import type {
  ArticleMetadata,
  ArticleSelectors,
  NewsPluginConfig,
} from './types';
import {
  DEFAULT_ARTICLE_SELECTORS,
} from './types';
import {
  $,
  $$,
  getText,
  getAttr,
  getJsonLd,
  getOpenGraph,
  getMeta,
} from '../base';
import { resolveTaxonomy, applyCategoryMap } from './taxonomy';

// Longest plausible category/tag label extracted from the DOM. Anything longer
// is a selector that matched a layout container, not a label (dir.bg's generic
// `.section` matched the whole-article div → 100KB "categories" in profiles).
const MAX_CATEGORY_LENGTH = 100;

// =============================================================================
// PAGE TYPE DETECTION
// =============================================================================

/**
 * Detect if the current page is an article
 */
export function isArticlePage(selectors: ArticleSelectors = {}): boolean {
  const merged = { ...DEFAULT_ARTICLE_SELECTORS, ...selectors };

  // Never treat the homepage or known listing/utility pages as an article — even
  // when a teaser `<article>` element matches the selector (the cause of homepages
  // being tracked as `Article Viewed`). Negative-only: real articles never live at
  // these paths, so this can't produce false negatives. `password|parola|reset|forgot`: 24chasa's
  // `/password/reset/<token>` carried article markup and was tracked as an article with the token in
  // its URL (~930 events, 22.06–26.10; NEWS-LOGIN-IDENTITY-2026-10-26 §4).
  const path = (typeof window !== 'undefined' && window.location ? window.location.pathname : '') || '';
  if (path === '' || path === '/' || /^\/index\.\w+$/i.test(path)) return false;
  if (/(^|\/)(category|categories|section|sections|tag|tags|topic|topics|author|authors|search|archive|archives|login|register|signin|signup|profil|profile|account|logout|rss|sitemap|password|parola|reset|forgot)(\/|$)/i.test(path)) {
    return false;
  }

  // Check if article container exists
  if (merged.articlePage) {
    const articleElement = $(merged.articlePage);
    if (articleElement) {
      return true;
    }
  }

  // Check JSON-LD for Article schema
  const jsonLdArticle = getJsonLd<{ '@type'?: string }>('Article');
  const jsonLdNews = getJsonLd<{ '@type'?: string }>('NewsArticle');
  const jsonLdBlog = getJsonLd<{ '@type'?: string }>('BlogPosting');

  if (jsonLdArticle || jsonLdNews || jsonLdBlog) {
    return true;
  }

  // Check Open Graph type
  const ogType = getMeta('og:type');
  if (ogType === 'article') {
    return true;
  }

  return false;
}

/**
 * Get page type (article, category, homepage, etc.)
 */
export function getPageType(selectors: ArticleSelectors = {}): string {
  if (isArticlePage(selectors)) {
    return 'article';
  }

  const path = window.location.pathname;

  // Check common patterns
  if (path === '/' || path === '/index.html') {
    return 'homepage';
  }

  if (/\/(category|section|tag|topic)\//i.test(path)) {
    return 'category';
  }

  if (/\/(search|buscar|szukaj)\//i.test(path)) {
    return 'search';
  }

  if (/\/(author|autor|writer)\//i.test(path)) {
    return 'author';
  }

  if (/\/(archive|archives|archiwum)\//i.test(path)) {
    return 'archive';
  }

  return 'other';
}

// =============================================================================
// METADATA EXTRACTION
// =============================================================================

/**
 * Extract article metadata from the page
 */
export function extractArticleMetadata(
  config: NewsPluginConfig = {}
): ArticleMetadata | null {
  const selectors = { ...DEFAULT_ARTICLE_SELECTORS, ...config.selectors };

  // Start with basic URL info
  const metadata: ArticleMetadata = {
    title: '',
    url: window.location.href,
    path: window.location.pathname,
  };

  // Try to extract from JSON-LD first (most reliable)
  const jsonLdMetadata = extractFromJsonLd();
  if (jsonLdMetadata) {
    Object.assign(metadata, jsonLdMetadata);
  }

  // Later extractors only fill fields the earlier ones left empty — with one
  // exception, and it is the field that broke.
  //
  // dir.bg's JSON-LD carries `datePublished: "2026-07-25"`: the day, with no
  // time of day. The page's own `<time datetime>` carries the hour, and
  // `extractFromDOM` below reads exactly that — but it never ran for this
  // field, because `publishedAt` was already "filled". A date is not an
  // answer here, it is half of one: parsed, it becomes midnight, and midnight
  // is a confident wrong number rather than a missing one.
  //
  // Production, 2026-07-27: 3330 of 3330 `dnes.dir.bg` articles sat at exactly
  // midnight, which made an article's computed age a function of what time of
  // day it happened to be read (age ≈ hours since midnight), and the age
  // bucket is what every Newsroom Score percentile is measured against.
  //
  // So a date-only `publishedAt` keeps the search open.
  const isDateOnly = (v: unknown): boolean =>
    typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim());
  const stillWanted = (key: string, current: unknown): boolean =>
    !current || (key === 'publishedAt' && isDateOnly(current));

  const fillGaps = (source: Partial<ArticleMetadata> | null | undefined): void => {
    if (!source) return;
    for (const [key, value] of Object.entries(source)) {
      if (value && stillWanted(key, metadata[key as keyof ArticleMetadata])) {
        (metadata as unknown as Record<string, unknown>)[key] = value;
      }
    }
  };

  // Structured data first, then Open Graph → meta tags → DOM elements, each
  // filling only what is still missing. Microdata sits directly behind JSON-LD
  // because it is the same kind of claim — the publisher stating the byline —
  // rather than a selector guess about which element looks author-shaped.
  fillGaps(extractFromMicrodata());
  fillGaps(extractFromOpenGraph());
  fillGaps(extractFromMetaTags());
  fillGaps(extractFromDOM(selectors, config.categoryMap));

  // URL→category taxonomy — the primary category source for sites (e.g.
  // 24chasa.bg) whose section lives only in the URL path, not the markup. Fills
  // category/section/articleType only where markup/structured data left a gap.
  const taxonomy = resolveTaxonomy(config.urlTaxonomy, metadata.path, metadata.url);
  if (!metadata.category && taxonomy.category) metadata.category = taxonomy.category;
  if (!metadata.section && taxonomy.section) metadata.section = taxonomy.section;
  if (!metadata.articleType && taxonomy.contentType) metadata.articleType = taxonomy.contentType;

  // Normalise the category via the slug→display map (covers markup- and
  // URL-derived values alike).
  metadata.category = applyCategoryMap(metadata.category, config.categoryMap);

  // Forced language from the profile when the page markup is unreliable
  // (24chasa.bg, for instance, declares <html lang="en"> on Bulgarian content).
  if (!metadata.language && config.language) metadata.language = config.language;

  // Calculate word count and reading time if we have content
  if (!metadata.wordCount && selectors.articleContent) {
    const contentElement = $(selectors.articleContent);
    if (contentElement) {
      const text = contentElement.textContent || '';
      metadata.wordCount = countWords(text);
      metadata.readingTimeMinutes = estimateReadingTime(metadata.wordCount);
    }
  }

  // Title is required
  if (!metadata.title) {
    metadata.title = document.title;
  }

  // Generate articleId from path if not already set
  if (!metadata.articleId) {
    metadata.articleId = generateArticleId(metadata.path, metadata.url);
  }

  // Add site info
  if (config.siteName) {
    metadata.custom = {
      ...metadata.custom,
      siteName: config.siteName,
    };
  }
  if (config.siteId) {
    metadata.custom = {
      ...metadata.custom,
      siteId: config.siteId,
    };
  }

  return metadata;
}

/**
 * Generate a unique article ID from the path/URL
 * Uses the path without query strings and trailing slashes
 */
function generateArticleId(path: string, url: string): string {
  // Remove query string and hash
  let cleanPath = path.split('?')[0].split('#')[0];

  // Remove trailing slash
  cleanPath = cleanPath.replace(/\/+$/, '');

  // Remove leading slash for consistency
  cleanPath = cleanPath.replace(/^\/+/, '');

  // Collapse the duplicate URL schemes a CMS serves the same article under.
  // 24chasa answers on both /bulgaria/article/23290333 and
  // /index.php/bulgaria/article/23290333, and without this each becomes its own
  // article: 2,750 phantom articles on that site, 7.35% of its catalogue,
  // measured on production 2026-07-31. The server canonicalises the same way at
  // ingest (utils/article-id.ts :: canonicalArticleId) — that is the guarantee,
  // since SDK bundles roll out slowly; this keeps the id right at the source.
  cleanPath = cleanPath.replace(/^index\.(?:php|html?)\//i, '').replace(/^amp\//i, '');

  // If path is empty (homepage), use the hostname
  if (!cleanPath) {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return 'homepage';
    }
  }

  return cleanPath;
}

/**
 * Extract metadata from JSON-LD structured data
 */
function extractFromJsonLd(): Partial<ArticleMetadata> | null {
  interface JsonLdArticle {
    '@type'?: string;
    headline?: string;
    name?: string;
    description?: string;
    author?: { name?: string } | { name?: string }[] | string;
    datePublished?: string;
    dateModified?: string;
    articleSection?: string;
    keywords?: string | string[];
    image?: string | { url?: string } | { url?: string }[];
    wordCount?: number;
    inLanguage?: string;
    isAccessibleForFree?: boolean;
    articleBody?: string;
  }

  // Try different article types
  const article =
    getJsonLd<JsonLdArticle>('Article') ||
    getJsonLd<JsonLdArticle>('NewsArticle') ||
    getJsonLd<JsonLdArticle>('BlogPosting') ||
    getJsonLd<JsonLdArticle>('WebPage');

  if (!article) {
    return null;
  }

  const metadata: Partial<ArticleMetadata> = {};

  // Title
  if (article.headline || article.name) {
    metadata.title = article.headline || article.name;
  }

  // Author
  if (article.author) {
    if (typeof article.author === 'string') {
      metadata.author = article.author;
    } else if (Array.isArray(article.author)) {
      metadata.author = article.author
        .map((a) => (typeof a === 'string' ? a : a.name))
        .filter(Boolean)
        .join(', ');
    } else if (article.author.name) {
      metadata.author = article.author.name;
    }
  }

  // Published date
  if (article.datePublished) {
    metadata.publishedAt = article.datePublished;
  }

  // Category
  if (article.articleSection) {
    metadata.category = article.articleSection;
  }

  // Tags/keywords
  if (article.keywords) {
    if (typeof article.keywords === 'string') {
      metadata.tags = article.keywords.split(',').map((t) => t.trim());
    } else {
      metadata.tags = article.keywords;
    }
  }

  // Image
  if (article.image) {
    if (typeof article.image === 'string') {
      metadata.imageUrl = article.image;
    } else if (Array.isArray(article.image)) {
      const first = article.image[0];
      metadata.imageUrl = typeof first === 'string' ? first : first?.url;
    } else {
      metadata.imageUrl = article.image.url;
    }
  }

  // Description/excerpt
  if (article.description) {
    metadata.excerpt = article.description;
  }

  // Word count
  if (article.wordCount) {
    metadata.wordCount = article.wordCount;
    metadata.readingTimeMinutes = estimateReadingTime(article.wordCount);
  }

  // Language
  if (article.inLanguage) {
    metadata.language = article.inLanguage;
  }

  // Premium content
  if (article.isAccessibleForFree === false) {
    metadata.isPremium = true;
  }

  // Article type
  if (article['@type']) {
    metadata.articleType = article['@type'];
  }

  return metadata;
}

/**
 * Extract metadata from Open Graph tags
 */
function extractFromOpenGraph(): Partial<ArticleMetadata> {
  const og = getOpenGraph();
  const metadata: Partial<ArticleMetadata> = {};

  if (og.title) {
    metadata.title = og.title;
  }

  if (og.description) {
    metadata.excerpt = og.description;
  }

  if (og.image) {
    metadata.imageUrl = og.image;
  }

  // article:author
  const author = getMeta('article:author');
  if (author) {
    metadata.author = author;
  }

  // article:section
  const section = getMeta('article:section');
  if (section) {
    metadata.category = section;
  }

  // article:published_time
  const published = getMeta('article:published_time');
  if (published) {
    metadata.publishedAt = published;
  }

  // article:tag (can be multiple)
  const tags = $$('meta[property="article:tag"]');
  if (tags.length > 0) {
    metadata.tags = tags.map((t) => getAttr(t, 'content')).filter(Boolean) as string[];
  }

  return metadata;
}

/**
 * Extract metadata from standard meta tags
 */
/**
 * schema.org MICRODATA — the layer between JSON-LD and OG/meta.
 *
 * 24chasa publishes no NewsArticle JSON-LD and no `meta[name=author]`, but does
 * mark bylines up as microdata. Measured against two live article pages on
 * 2026-07-31, the markup is:
 *
 *   <span style="display:none;"><span itemprop="author" content="ИМЕ"></span>
 *   <h2 itemprop="author" itemtype=".../Person">
 *     <a href="/authors/31355" itemprop="name" content="24 часа"></a>
 *
 * Two traps, and the crawler hit both. The FIRST `[itemprop="author"]` in the
 * DOM is the hidden span, which on some pages is entirely empty — so a
 * `.first()` read returns nothing. And on the house account the anchor carries
 * NO TEXT AT ALL: the name exists only in a `content` attribute, invisible to
 * any text-only extraction. That is 72% of one client's articles.
 *
 * So: scan every candidate, and read each by precision — nested itemprop=name
 * text, then the microdata `content` attributes, then plain text. Mirrors
 * enrichment-worker/src/fetcher.ts; the two must not drift, or the byline the
 * SDK reports and the byline the crawler stores describe different people.
 */
function extractFromMicrodata(): Partial<ArticleMetadata> {
  const out: Partial<ArticleMetadata> = {};
  for (const el of $$('[itemprop="author"]')) {
    const nameEl = el.querySelector('[itemprop="name"]');
    const value = (
      (nameEl && getText(nameEl)) ||
      (nameEl && getAttr(nameEl, 'content')) ||
      getAttr(el, 'content') ||
      getText(el) ||
      ''
    ).trim();
    if (value) {
      out.author = value;
      break;
    }
  }
  return out;
}

function extractFromMetaTags(): Partial<ArticleMetadata> {
  const metadata: Partial<ArticleMetadata> = {};

  // Description
  const description = getMeta('description');
  if (description) {
    metadata.excerpt = description;
  }

  // Author
  const author = getMeta('author');
  if (author) {
    metadata.author = author;
  }

  // Keywords (as tags)
  const keywords = getMeta('keywords');
  if (keywords) {
    metadata.tags = keywords.split(',').map((k) => k.trim());
  }

  return metadata;
}

/**
 * Extract metadata from DOM elements using selectors
 */
function extractFromDOM(
  selectors: ArticleSelectors,
  categoryMap?: Record<string, string>
): Partial<ArticleMetadata> {
  const metadata: Partial<ArticleMetadata> = {};

  // Title
  if (selectors.articleTitle) {
    const titleEl = $(selectors.articleTitle);
    const title = getText(titleEl);
    if (title) {
      metadata.title = title;
    }
  }

  // Author
  if (selectors.articleAuthor) {
    const authorEl = $(selectors.articleAuthor);
    const author = getText(authorEl);
    if (author) {
      metadata.author = author;
    }
  }

  // Category
  if (selectors.articleCategory) {
    const categoryEl = $(selectors.articleCategory);
    let category = getText(categoryEl);
    // A category is a short label. A long or multiline match means the
    // selector hit a layout container (e.g. dir.bg's div.section = the whole
    // article) — drop it so the URL-taxonomy fallback can apply instead.
    if (category && (category.length > MAX_CATEGORY_LENGTH || category.includes('\n'))) {
      category = null;
    }
    if (category) {
      // Apply category mapping if available
      if (categoryMap && categoryMap[category.toLowerCase()]) {
        category = categoryMap[category.toLowerCase()];
      }
      metadata.category = category;
    }
  }

  // Date.
  //
  // Deliberately scans the matches rather than taking `$(selector)`'s first
  // hit. 24chasa renders `<time class="time" datetime="">` — empty — ahead of
  // the real `<time class="time" datetime="2026-07-27 18:08">`, so first-match
  // yields nothing at all. This is the same shape as the byline trap on that
  // site, where a naive `[class*="author"]` selector matched the photographer.
  //
  // Preference order within the matches: a value carrying a time of day beats
  // a bare date, because a bare date parses to midnight and midnight is a
  // confident wrong answer (see `stillWanted` in extractArticleMetadata).
  if (selectors.articleDate) {
    const looksDated = (v: string): boolean => /\d{4}-\d{2}-\d{2}/.test(v);
    const hasTimeOfDay = (v: string): boolean => /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(v);

    let dateOnlyFallback = '';
    let textFallback = '';

    for (const el of $$(selectors.articleDate)) {
      const datetime = (getAttr(el, 'datetime') || '').trim();
      if (datetime && looksDated(datetime)) {
        if (hasTimeOfDay(datetime)) {
          metadata.publishedAt = datetime;
          break;
        }
        if (!dateOnlyFallback) dateOnlyFallback = datetime;
        continue;
      }
      if (!textFallback) {
        const parsed = parseDate(getText(el) || '');
        if (parsed) textFallback = parsed;
      }
    }

    if (!metadata.publishedAt) {
      metadata.publishedAt = dateOnlyFallback || textFallback || metadata.publishedAt;
    }
  }

  // Tags
  if (selectors.articleTags) {
    const tagElements = $$(selectors.articleTags);
    if (tagElements.length > 0) {
      const tags = tagElements
        .map((el) => getText(el))
        .filter((t): t is string => !!t && t.length <= MAX_CATEGORY_LENGTH && !t.includes('\n'));
      if (tags.length > 0) {
        metadata.tags = tags;
      }
    }
  }

  // Image
  if (selectors.articleImage) {
    const imgEl = $(selectors.articleImage);
    if (imgEl) {
      const src = getAttr(imgEl, 'src') || getAttr(imgEl, 'data-src');
      if (src) {
        metadata.imageUrl = src.startsWith('http') ? src : new URL(src, window.location.origin).href;
      }
    }
  }

  // Excerpt
  if (selectors.articleExcerpt) {
    const excerptEl = $(selectors.articleExcerpt);
    const excerpt = getText(excerptEl);
    if (excerpt) {
      metadata.excerpt = excerpt;
    }
  }

  return metadata;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Count words in text
 */
function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
}

/**
 * Estimate reading time in minutes
 */
function estimateReadingTime(wordCount: number, wordsPerMinute = 200): number {
  return Math.ceil(wordCount / wordsPerMinute);
}

/**
 * Try to parse a date string into ISO format
 */
function parseDate(dateString: string): string | null {
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

/**
 * Get article content element
 */
export function getArticleContentElement(selectors: ArticleSelectors = {}): Element | null {
  const merged = { ...DEFAULT_ARTICLE_SELECTORS, ...selectors };
  return $(merged.articleContent || '');
}

/**
 * Calculate article content height
 */
export function getArticleContentHeight(selectors: ArticleSelectors = {}): number {
  const element = getArticleContentElement(selectors);
  if (!element) {
    return document.body.scrollHeight;
  }
  return element.scrollHeight;
}
